import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Typography, Container, Stepper, Step, StepLabel, Card, CardContent, Snackbar, Alert } from '@mui/material';
import { AutoAwesome as AIIcon } from '@mui/icons-material';
import axios from 'axios';

// Components
import AIImageUpload from '../components/ai-trade/AIImageUpload';
import AITradeForm from '../components/ai-trade/AITradeForm';
import AITradePreview from '../components/ai-trade/AITradePreview';

// Hooks
import { useImageAnalysis } from '../hooks/useImageAnalysis';
import { useSettings } from '../contexts/SettingsContext';
import alertService from '../services/alertService';

const AITradeCreate = () => {
  const location = useLocation();
  const { settings } = useSettings();
  const [strategies, setStrategies] = useState([]);
  const [trades, setTrades] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [currentStep, setCurrentStep] = useState(0);

  const {
    imageFile, previewUrl, tradeData, setTradeData, loadingModel, metrics,
    handleFileSelect, handleDrop, analyzeImage, createTrade, clearImage
  } = useImageAnalysis();

  const steps = ['Upload Screenshot', 'AI Analysis', 'Trade Details', 'Review & Save'];

  useEffect(() => {
    fetchStrategies();
    fetchTrades();
    
    if (location.state?.editTrade) {
      handleEditTrade(location.state.editTrade);
    }
  }, [location.state]);

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategies(res.data);
    } catch (err) {
      console.error('Error fetching strategies:', err);
    }
  };

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrades(res.data.sort((a, b) => new Date(b.tradeDate) - new Date(a.tradeDate)));
    } catch (err) {
      console.error('Error fetching trades:', err);
    }
  };

  const handleEditTrade = useCallback((trade) => {
    const now = new Date();
    const defaultDate = now.toISOString().split('T')[0];
    const defaultTime = now.toTimeString().split(' ')[0].slice(0, 5);
    
    setTradeData({
      ...trade,
      category: trade.category || 'stock',
      leverage: Number(trade.leverage) || 1,
      positionSize: Number(trade.positionSize) || '',
      strategyIds: trade.strategyIds || [],
      tags: trade.tags || '',
      tradeDate: trade.tradeDate || defaultDate,
      tradeTime: trade.tradeTime || defaultTime,
      closeDate: trade.closeDate || '',
      closeTime: trade.closeTime || '',
      rating: trade.rating || 3,
      exchange: trade.exchange || ''
    });
  }, []);

  const handleAnalyze = useCallback(async () => {
    // Check AI quota limits
    const aiQuota = settings?.subscription?.aiTradeAssistQuota;
    if (aiQuota && aiQuota.used >= aiQuota.totalLimit) {
      setSnackbar({ 
        open: true, 
        message: `AI analysis limit reached (${aiQuota.used}/${aiQuota.totalLimit}). Upgrade your plan for more.`, 
        severity: 'warning' 
      });
      return;
    }
    
    const result = await analyzeImage();
    setSnackbar({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
    if (result.success) {
      setCurrentStep(2); // Move to trade details step
    }
  }, [analyzeImage, settings?.subscription?.aiTradeAssistQuota]);

  const handleSaveTrade = useCallback(async () => {
    const result = await createTrade();
    setSnackbar({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
    if (result.success) {
      fetchTrades();
      // Update alerts after creating trade
      alertService.updateAlerts([...trades, tradeData]);
      setCurrentStep(0); // Reset to start
    }
  }, [createTrade, fetchTrades, trades, tradeData]);

  const handleTradeDataChange = useCallback((field, value) => {
    setTradeData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleNext = () => {
    if (currentStep === 0 && imageFile) {
      setCurrentStep(1);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <AIImageUpload
            previewUrl={previewUrl}
            onFileSelect={handleFileSelect}
            onDrop={handleDrop}
            hasImage={!!imageFile}
            onNext={() => setCurrentStep(1)}
            onClear={clearImage}
          />
        );
      case 1:
        return (
          <Card elevation={3} sx={{ textAlign: 'center', p: { xs: 2, sm: 3, md: 4 } }}>
            <CardContent>
              <AIIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                AI Analysis Ready
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Click analyze to extract trade data from your screenshot
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Usage: {settings?.subscription?.aiTradeAssistQuota?.used || 0}/{settings?.subscription?.aiTradeAssistQuota?.totalLimit || 0}
                {!settings?.subscription?.aiTradeAssistQuota && ' (loading...)'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  onClick={handleAnalyze}
                  disabled={loadingModel}
                  variant="contained"
                  size="large"
                  sx={{ minWidth: 150 }}
                >
                  {loadingModel ? 'Analyzing...' : 'Analyze Screenshot'}
                </Button>
                <Button
                  onClick={() => setCurrentStep(2)}
                  variant="outlined"
                  size="large"
                  sx={{ minWidth: 150 }}
                >
                  Skip Analysis
                </Button>
              </Box>
            </CardContent>
          </Card>
        );
      case 2:
        return (
          <AITradeForm
            tradeData={tradeData}
            onChange={handleTradeDataChange}
            strategies={strategies}
            trades={trades}
            metrics={metrics}
          />
        );
      case 3:
        return (
          <AITradePreview
            tradeData={tradeData}
            previewUrl={previewUrl}
            metrics={metrics}
            onSave={handleSaveTrade}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
      <Box mb={4} sx={{ textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          <AIIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
          Create Trade with AI
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload, analyze, and create trades with AI assistance
        </Typography>
      </Box>

      <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Box sx={{ minHeight: 400, mb: 4 }}>
        {renderStepContent()}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
        <Button
          onClick={handleBack}
          disabled={currentStep === 0}
          variant="outlined"
          size="large"
          fullWidth
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Back
        </Button>
        
        {currentStep < 3 && currentStep !== 1 && (
          <Button
            onClick={handleNext}
            disabled={
              (currentStep === 0 && !imageFile) ||
              (currentStep === 2 && (!tradeData.symbol || !tradeData.entryPrice))
            }
            variant="contained"
            size="large"
            fullWidth
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Next
          </Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AITradeCreate;